<template>
    <div>
        <ds-modal
            show-close
            is-open
            class="file-upload-modal"
            :title="$t('unlayerBuilder.fileUpload.title')"
            @close="$emit('close')"
        >
            <DsInlineAlert
                v-if="showEmptyFileAlert && !selectedFileId"
                leading-icon
                type="critical"
                class="unselected-file-alert"
            >
                {{ $t('unlayerBuilder.fileUpload.error.noFileSelected') }}
            </DsInlineAlert>

            <section>
                <ds-file-upload-input
                    id="file-upload-input"
                    ref="input"
                    class="file-upload-input"
                    :value="file"
                    :max-file-size-bytes="10485760"
                    accept=".pdf,.xlsx"
                    :label="$t('unlayerBuilder.fileUpload.uploadInputLabel')"
                    :allowed-files-label="$t('unlayerBuilder.fileUpload.allowedFilesLabel')"
                    @input="handleInput"
                    @error="handleError"
                />
            </section>

            <section
                v-if="files.length"
                ref="filesScrollContainer"
                class="file-list"
                data-qa="file-list"
            >
                <div class="select-file">
                    {{ $t('unlayerBuilder.fileUpload.selectAFile') }}
                </div>

                <FileUploadListItem
                    v-for="({ name, url, lastUpdated, id }, i) in filteredFiles"
                    :key="i"
                    :file-name="name"
                    :file-url="url"
                    :file-last-updated="lastUpdated"
                    :file-id="id"
                    :is-selected="selectedFileId === id"
                    :selected-file-id="selectedFileId || ''"
                    class="file-upload-list-item"
                    @select="(file) => selectedFile = file"
                    @delete="handleFileDelete(id)"
                />

                <ScrollTarget
                    v-if="showAllFiles && !isLoading && !didLoadAllFiles"
                    :root="$refs.filesScrollContainer"
                    :threshold="200"
                    @intersect="loadFiles"
                />

                <DsTextButton
                    v-if="showAllFilesButton"
                    data-qa="show-all-files-button"
                    @click="() => { showAllFiles = true; showAllFilesButton = false; }"
                >
                    {{ $t('unlayerBuilder.fileUpload.showAllFiles') }}
                </DsTextButton>
            </section>

            <ds-placeholder
                class="file-row-placeholder"
                :loading="isLoading"
                :rows="rows"
            />

            <footer
                class="file-upload-modal-footer"
            >
                <ds-filled-button
                    data-qa="file-upload-modal-footer-insert-button"
                    class="file-upload-modal-footer-insert-button"
                    @click="handleFileSelect"
                >
                    {{ $t('unlayerBuilder.fileUpload.insertFile') }}
                </ds-filled-button>

                <ds-outline-button
                    data-qa="file-upload-modal-footer-cancel-button"
                    @click="$emit('close')"
                >
                    {{ $t('unlayerBuilder.fileUpload.cancel') }}
                </ds-outline-button>
            </footer>
        </ds-modal>
    </div>
</template>

<script>
import axios from 'axios';
import FileUploadListItem from './FileUploadListItem';
import ScrollTarget from '@/components/unlayer/ScrollTarget.vue';
import { getUnlayerUserUploadedFiles } from '@/api/campaigns/campaignUnlayerEmail';
import sentry from '@/analytics/sentry';

const FILES_LIST_PAGE_SIZE = 15;

export default {
    components: {
        FileUploadListItem,
        ScrollTarget,
    },

    data() {
        return {
            isLoading: false,
            file: null,
            files: [],
            filesPageLimit: FILES_LIST_PAGE_SIZE,
            noMoreData: false,
            nextPageKey: null,
            selectedFile: {},
            showAllFiles: false,
            showAllFilesButton: true,
            showEmptyFileAlert: false,
            rows: [
                { height: '2rem', boxes: [3, 1] },
                { height: '2rem', boxes: [3, 1] },
                { height: '2rem', boxes: [3, 1] },
            ],
        };
    },

    created() {
        this.loadFiles();
    },

    computed: {
        selectedFileId() {
            return this.selectedFile?.id;
        },

        filteredFiles() {
            return this.showAllFiles ? this.files : this.files.slice(0, 5);
        },

        didLoadAllFiles() {
            return this.files.length && !this.nextPageKey;
        },
    },

    methods: {
        async loadFiles() {
            if (this.didLoadAllFiles) {
                this.isLoading = false;

                return;
            }

            this.isLoading = true;

            try {
                const { files, nextPageKey } = await getUnlayerUserUploadedFiles(this.nextPageKey);

                this.files = [...this.files, ...files];
                this.nextPageKey = nextPageKey;

                if (!this.nextPageKey) {
                    this.noMoreData = true;
                } else {
                    this.noMoreData = false;
                }
            } catch (error) {
                sentry.captureException(error);
                this.$error({
                    message: this.$t('unlayerBuilder.fileUpload.error.loadingFiles'),
                    bottom: true,
                });
            }

            this.isLoading = false;
        },

        async handleInput(file) {
            if (file === null) {
                return;
            }

            const ref = this.$refs.input;

            ref.complete = false;

            const formData = new FormData();

            formData.append('file', file);

            try {
                await axios.post('/app/unlayerEmailFunnel/file/upload', formData);

                ref.complete = true;
                ref.success = true;

                this.reloadFiles();
            } catch (e) {
                this.$error({
                    message: this.$t('unlayerBuilder.fileUpload.error.uploadingFiles'),
                    bottom: true,
                });
            }
        },

        handleError(error) {
            const ref = this.$refs.input;

            ref.error = true;
            ref.errorMessage = error === 'fileSize'
                ? this.$t('unlayerBuilder.fileUpload.error.maxFileSizeExceeded')
                : this.$t('unlayerBuilder.fileUpload.error.uploadingFiles');
            ref.uploadStarted = true;
        },

        reloadFiles() {
            this.files = [];
            this.nextPageKey = null;
            this.loadFiles();
        },

        handleFileSelect() {
            const { selectedFile, selectedFileId } = this;

            this.showEmptyFileAlert = !selectedFileId;

            if (this.showEmptyFileAlert) return;

            this.$emit('file-selected', selectedFile);
        },

        async handleFileDelete(fileId) {
            try {
                await axios.delete(`/app/unlayerEmailFunnel/file/${fileId}`);
            } catch (e) {
                sentry.captureException(e);
                this.$error({
                    message: this.$t('unlayerBuilder.fileUpload.error.deletingFiles'),
                    bottom: true,
                });

                return;
            }

            this.reloadFiles();
        },
    },
};
</script>

<style lang="scss" scoped>
.file-list {
    max-height: px-to-rem(550);
    min-height: px-to-rem(140);
    overflow-y: auto;
}

.file-upload-list-item {
    border-bottom: 1px solid $color-ink-200;

    &:last-of-type {
        border-bottom: 0;
    }
}

.file-row-placeholder {
    margin-top: $spacing-200;
}

.select-file {
    padding: $spacing-200 0;
    font-size: $font-size-lg;
}

.file-upload-modal-footer {
    border-top: px-to-rem(1) solid $color-gray-200;
    padding: $spacing-300;
    margin-top: $spacing-300;
}

.file-upload-modal-footer-insert-button {
    margin-right: $spacing-100;
}

.file-upload-modal {
    --modal-padding: 0;
    section {
        padding: 0 $spacing-300;
        &:first-child {
            padding-top: $spacing-300;
        }
    }
}

.file-upload-input::v-deep {
    .instructions, .allowedFilesLabel {
        text-transform: none;
    }
}

.unselected-file-alert {
    margin: $spacing-100 $spacing-300;
}
</style>
